import { getYear, parse } from "date-fns";
import { i18n } from "@rmp/core/plugins";
// @ts-ignore
import { checkINN, checkSnils, checkOgrn, checkOgrnip } from "ru-validation-codes";
// @ts-ignore
import isKpp from "is-kpp-js";
import { isDateWithSecondsFormatValid } from "@rmp/core/utils/formats";
import { formatDate } from "@rmp/core/utils/dates";
import validator from "is-my-date-valid";
import {
	validateLegalEntityMaskedInn,
	validateMaskedKpp,
	validateMaskedOgrn,
	validateMaskedOgrnip,
	validatePersonMaskedInn,
	validateBankAccountNumber,
	validateCorrespondentAccount,
	validateBankIdentificationCode, validateIMO, validateMMSI, validateMultipleOfHalf, validateTime, validTimeSpan
} from "@rmp/core/utils/validator";
import { isPhoneValid } from "@rmp/core/utils/phone";
import passwordValidator from "@rmp/core/utils/password";
import { parseFloatNumber } from "@rmp/core/utils/number";
import { getDurationTotal, parseTimeSpanToDuration } from "@rmp/core/utils/duration";
import { CalendarDate, calendarDateFromDisplay, formatCalendarDate } from "@rmp/core/types/calendarDate";
import { isLocalTime } from "@rmp/core/types/localTime";
import { combineLocalDateTime } from "@rmp/core/types/localDateTime";

const FIRSTNAME_REGEXP = /^[.(]?[А-ЯЁ]([\-\s.,(’]?([а-яА-ЯЁё])[\-\s.,)’]?){1,}[а-яА-ЯЁё]?[\s?IVX]{0,3}[.)]??$/;
const MIDDLE_NAME_REGEXP = /^[.(]?[А-ЯЁ]([\-\s.,(’]?([а-яА-ЯЁё])[\-\s.,)’]?){1,}[а-яА-ЯЁё]?[\s?IVX]{0,3}[.)]??$/;
const LASTNAME_REGEXP = /^[.(]?[А-ЯЁ]([\-\s.,(’]?([а-яА-ЯЁё])[\-\s.,)’]?){1,}[а-яА-ЯЁё]?[\s?IVX]{0,3}[.)]??$/;

const validateDateTime = validator({ format: isDateWithSecondsFormatValid });

// Vuetify сравнивает старый и новый массив :rules через deepEqual, а у функций нет
// собственных перечисляемых свойств — поэтому любые две разные функции считаются равными
// и watcher на rules не запускает повторную валидацию. Явно проставляем зависимости как
// свойство функции, чтобы deepEqual увидел разницу и Vuetify перевалидировал поле сам,
// без ручного form.validateField().
export const withRuleDeps = <T extends (...args: any[]) => any>(rule: T, deps: unknown[]): T => {
	Object.assign(rule, { __deps: deps });
	return rule;
};

export const requiredRule = () => {
	return (v: string | number | null | undefined) =>
		v !== null && v !== undefined && String(v) !== "" && !/^[\s]+$/.test(String(v))
		|| i18n.t("validation.required");
};

export const nonEmptyArrayRule = () => {
	return (v: any[]) =>
		Array.isArray(v) && v.length > 0 || i18n.t("validation.required");
};

// RmpDropzone валидирует не сам файл, а факт его наличия — отдаёт булево, а не строку/число,
// поэтому requiredRule() тут не подходит: String(false) — непустая строка, проверка всегда бы проходила.
export const requiredFileRule = () => {
	return (v: boolean) =>
		!!v || i18n.t("validation.required");
};

export const requiredAutocompleteValueRule = (requiredKey: string) => {
	return (v: any) => v !== null && !!v[requiredKey] || i18n.t("validation.required");
};

export const requiredMultipleAutocompleteRule = () => {
	return (v: any) => v !== null && !!v.length || i18n.t("validation.required");
};

export const prepareInnRule = () => {
	return (v: any) => !v || checkINN(v) || i18n.t("validation.inn");
};

export const prepareLegalEntityInnRule = () => {
	return (v: string | any[]) => !v || (checkINN(v) && v.length === 10) || i18n.t("validation.inn");
};

export const prepareLegalEntityMaskedInnValidationRule = () => {
	return (v: string) => !v || validateLegalEntityMaskedInn(v) || i18n.t("validation.inn");
};

export const preparePersonMaskedInnValidationRule = () => {
	return (v: string) => !v || validatePersonMaskedInn(v) || i18n.t("validation.inn");
};

export const prepareMaskedKppValidationRule = () => {
	return (v: string) => !v || validateMaskedKpp(v) || i18n.t("validation.kpp");
};

export const prepareBankAccountNumberValidationRule = (getBik: () => string) => {
	return (v: string) => !v || validateBankAccountNumber(v, getBik()) || i18n.t("validation.bankAccountNumber");
};

export const prepareCorrespondentAccountValidationRule = (getBik: () => string) => {
	return (v: string) => !v || validateCorrespondentAccount(v, getBik()) || i18n.t("validation.correspondentAccount");
};

export const prepareBankIdentificationCodeValidationRule = () => {
	return (v: string) => !v || validateBankIdentificationCode(v) || i18n.t("validation.bankIdentificationCode");
};

export const prepareSnilsRule = () => {
	return (v: any) => !v || checkSnils(v) || i18n.t("validation.snils");
};

export const prepareOgrnRule = () => {
	return (v: any) => !v || checkOgrn(v) || i18n.t("validation.ogrn");
};

export const prepareMaskedOgrnValidationRule = () => {
	return (v: string) => !v || validateMaskedOgrn(v) || i18n.t("validation.ogrn");
};

export const prepareMaskedOgrnipValidationRule = () => {
	return (v: string) => !v || validateMaskedOgrnip(v) || i18n.t("validation.ogrnip");
};

export const prepareOgrnipRule = () => {
	return (v: any) => !v || checkOgrnip(v) || i18n.t("validation.ogrnip");
};

export const prepareKppRule = () => {
	return (v: any) => !v || isKpp(v) || i18n.t("validation.kpp");
};

export const prepareMaxLengthRule = ({ maxLength }: { maxLength: number }) => {
	return (v: string | number | any[]) => !v || String(v).length <= maxLength || `${i18n.t("validation.maxLength", { number: maxLength })}`;
};

export const prepareMinLengthRule = ({ minLength }: { minLength: number }) => {
	return (v: string | any[]) => !v || v.length >= minLength || `${i18n.t("validation.minLength", { number: minLength })}`;
};

export const validDate = () => {
	return (v: string) => !v || validateDateTime(v) || i18n.t("validation.invalidDate");
};

export const validCalendarDate = () => {
	return (value: string) => !value || calendarDateFromDisplay(value) !== null || i18n.t("validation.invalidDate");
};

export const prepareMaxNumbersRule = ({ getMax, relatedFieldLabel }: { getMax: any, relatedFieldLabel?: string }) => {
	return (v: string) => !getMax() || !v || parseFloatNumber(v) <= getMax() ||
		(relatedFieldLabel
			? i18n.t("validation.maxNumberWithField", { number: getMax(), field: relatedFieldLabel })
			: i18n.t("validation.maxNumber", { number: getMax() }));
};

export const prepareExcludedMaxNumbersRule = ({ getMax, relatedFieldLabel }: { getMax: any, relatedFieldLabel?: string }) => {
	return (v: string) => !getMax() || !v || parseFloatNumber(v) < getMax() ||
		(relatedFieldLabel
			? i18n.t("validation.lessThanField", { number: getMax(), field: relatedFieldLabel })
			: i18n.t("validation.lessThan", { number: getMax() }));
};

export const prepareMinNumbersRule = ({ getMin }: { getMin: any }) => {
	return (v: string) => !v || parseFloatNumber(v) >= getMin() || i18n.t("validation.minNumber", { number: getMin() });
};

export const prepareExcludedMinNumbersRule = ({ getMin }: { getMin: any }) => {
	return (v: string) => !v || parseFloatNumber(v) > getMin() || i18n.t("validation.greaterThan", { number: getMin() });
};

export const prepareMaxDateRule = ({ getMax, format }: { getMax: any, format: string }) => {
	return (v: string) => !getMax() || !v || parse(v, format, new Date()) <= getMax() ||
		i18n.t("validation.maxDate", { number: formatDate(getMax(), format) });
};

export const prepareMinDateRule = ({ getMin, format }: { getMin: any, format: string }) => {
	return (v: string) => !getMin() || !v || parse(v, format, new Date()) >= getMin() ||
		i18n.t("validation.minDate", { number: formatDate(getMin(), format) });
};

export const prepareMaxCalendarDateRule = ({ getMax }: { getMax: () => CalendarDate | null | undefined }) => {
	return (value: string) => {
		const max = getMax();
		const current = calendarDateFromDisplay(value);
		return !max || !value || (!!current && current <= max) ||
			i18n.t("validation.maxDate", { number: formatCalendarDate(max) });
	};
};

export const prepareMinCalendarDateRule = ({ getMin }: { getMin: () => CalendarDate | null | undefined }) => {
	return (value: string) => {
		const min = getMin();
		const current = calendarDateFromDisplay(value);
		return !min || !value || (!!current && current >= min) ||
			i18n.t("validation.minDate", { number: formatCalendarDate(min) });
	};
};

export const prepareTimeBeforeRule = (getEndTime: () => string | null | undefined) => {
	return (value: string) => {
		const endTime = getEndTime();
		return !value || !endTime || !isLocalTime(value) || !isLocalTime(endTime) || value < endTime ||
			i18n.t("errors.serviceApplicationTillAfterFrom");
	};
};

export const prepareTimeAfterRule = (getStartTime: () => string | null | undefined) => {
	return (value: string) => {
		const startTime = getStartTime();
		return !value || !startTime || !isLocalTime(value) || !isLocalTime(startTime) || value > startTime ||
			i18n.t("errors.serviceApplicationTillAfterFrom");
	};
};

type LocalDateTimeComparisonRuleOptions = {
	currentDate: CalendarDate | null | undefined;
	otherDate: CalendarDate | null | undefined;
	otherTime: string | null | undefined;
};

export const prepareLocalDateTimeBeforeRule = ({
	currentDate,
	otherDate,
	otherTime
}: LocalDateTimeComparisonRuleOptions) => {
	return (value: string) => !value
		|| !currentDate
		|| !otherDate
		|| !isLocalTime(value)
		|| !isLocalTime(otherTime)
		|| combineLocalDateTime(currentDate, value) < combineLocalDateTime(otherDate, otherTime)
		|| i18n.t("errors.serviceApplicationTillAfterFrom");
};

export const prepareLocalDateTimeAfterRule = ({
	currentDate,
	otherDate,
	otherTime
}: LocalDateTimeComparisonRuleOptions) => {
	return (value: string) => !value
		|| !currentDate
		|| !otherDate
		|| !isLocalTime(value)
		|| !isLocalTime(otherTime)
		|| combineLocalDateTime(currentDate, value) > combineLocalDateTime(otherDate, otherTime)
		|| i18n.t("errors.serviceApplicationTillAfterFrom");
};

export const prepareYearRule = () => {
	return (v: number) => !v || (v > 0 && v <= getYear(new Date())) || i18n.tc("validation.year");
};

export const prepareIMORule = () => {
	return (v: string) => !v || validateIMO(v) || i18n.tc("validation.imo");
};

export const prepareMMSIRule = () => {
	return (v: string) => !v || validateMMSI(v) || i18n.tc("validation.mmsi");
};

export const prepareCalculationObjectCodeRule = () => {
	return (v: string) => !v || /^K[A-Z](\d){9}$/.test(v) || i18n.tc("validation.calculationObjectCode");
};

export const prepareEmailRule = () => {
	return (v: string) => !v || /.+@.+\..+/i.test(v) || i18n.t("validation.validEmail");
};

export const prepareLastnameRule = () => {
	return (v: string) => !v || LASTNAME_REGEXP.test(v) || i18n.t("validation.validLastname");
};

export const prepareFirstnameRule = () => {
	return (v: string) => !v || FIRSTNAME_REGEXP.test(v) || i18n.t("validation.validFirstname");
};

export const prepareMiddleNameRule = () => {
	return (v: string) => !v || MIDDLE_NAME_REGEXP.test(v) || i18n.t("validation.validMiddleName");
};

export const prepareTimeRule = () => {
	return (v: string) => !v || validateTime(v) || i18n.t("validation.validTime");
};

export const prepareTimeSpanRule = () => {
	return (v: string) => !v || validTimeSpan(v) || i18n.t("validation.validTime");
};

export const prepareNotZeroTimeRule = () => {
	return (v: string) => !v || !validateTime(v) || getDurationTotal(parseTimeSpanToDuration(v), "minutes") !== 0 || i18n.t("validation.notZeroTime");
};

export const prepareMaxTimeSpanRule = ({ maxMinutes }: { maxMinutes: number | undefined | null }) => {
	return (v: string) => {
		return maxMinutes == null || !v || !validTimeSpan(v) ||
			getDurationTotal(parseTimeSpanToDuration(v), "minutes") <= maxMinutes ||
			i18n.t("validation.maxTimeSpan");
	};
};

export const prepareUniqRule = ({ getValue, message }: { getValue: any, message: string }) => {
	return (v: any) => !v || getValue() !== v || message;
};

export const prepareConfirmedEmailRule = ({ getEmail }: { getEmail: any }) => {
	return (v: any) => !v || getEmail() === v || i18n.t("validation.validConfirmedEmail");
};

export const preparePasswordRule = () => {
	return (v: string) => {
		let result = passwordValidator.validate(v, { details: true }) as { message: string }[];
		return result.length ? result[0].message : true;
	};
};

export const prepareConfirmedPasswordRule = (getPassword: Function) => {
	return (v: string) => !v || !getPassword() || getPassword() === v || "Пароли не совпадают";
};

export const onlyIntegerNumbersRule = () => {
	return (v: string) => !v || /^[-\d]+$/.test(v) || i18n.t("validation.validOnlyIntegerNumbers");
};

export const onlyNumbersRule = () => {
	return (v: string) => !v || /^[0-9]+([.,]?\d+)*$/.test(v) || i18n.t("validation.validOnlyNumbers");
};

export const onlyMultipleOfHalfRule = () => {
	return (v: string) => !v || validateMultipleOfHalf(v) || i18n.t("validation.validOnlyMultipleOfHalfNumbers");
};

export const prepareTwoDecimalPlacesRule = () => {
	return (v: string) => !v || /^[0-9]+([.,]\d{1,2})?$/.test(v) || i18n.t("validation.twoDecimalPlaces");
};

export const onlyCurrencyRule = () => {
	return (v: string) => !v || /^\d+([.,]\d{1,2})?$/.test(v) || i18n.t("validation.validOnlyCurrency");
};

export const phoneNumberRule = ({ getCountry }: { getCountry: any }) => {
	return (v: string) => !v || (isPhoneValid({ value: v, country: getCountry() }) && /^[-\d\-\s+]+$/.test(v)) ||
		i18n.t("validation.validPhone");
};

export const onlyLatinRule = () => {
	return (v: string) => !v || /^[a-zA-Z\s-]+$/.test(v) || i18n.t("validation.validOnlyLatin");
};

export const onlyUppercaseLatinAndDigitsRule = () => {
	return (v: string) => !v || /^[A-Z0-9]+$/.test(v) || i18n.t("validation.validOnlyUppercaseLatinAndDigits");
};

/* Временные проверки */
export const passwordRule = () => {
	return (v: string | string[]) => !v || v.includes("test") || i18n.t("validation.validPassword");
};

/* Проверка на правильный ввод числа как положительного, так и отрицательного */
export const isValidPositiveAndNegativeNumberRule = () => (v: string | number) => {
	const trimmed = String(v).trim().replace(',', '.');
	return /^-?\d+(\.\d*)?$/.test(trimmed) || i18n.tc("validation.validOnlyNumbers");
};

export const prepareLongitudeRule = () => (v: string | number) => {
	const lon = parseFloat(String(v).trim().replace(',', '.'));
	if (lon < -180 || lon > 180) {
		return i18n.tc("validation.coordinates.longitudeRange");
	}
	return true;
};

export const prepareLatitudeRule = () => (v: string | number) => {
	const lat = parseFloat(String(v).trim().replace(',', '.'));
	if (lat < -90 || lat > 90) {
		return i18n.tc("validation.coordinates.latitudeRange");
	}
	return true;
};

export const prepareMaxDecimalPlacesRule = ({ maxPlaces }: { maxPlaces: number }) => {
	return (v: string | number) => {
		const strValue = String(v).trim().replace(',', '.');
		const decimalPart = strValue.split('.')[1];
		return !decimalPart || decimalPart.length <= maxPlaces || i18n.tc("validation.maxDecimalPlaces", maxPlaces);
	};
};
